import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";

type CB = { onSuccess?: (r: any) => void; onError?: (e: any) => void };

// ── Jobs ───────────────────────────────────────────────────────────────────

export const useJobList = (cid: number, params: any = {}) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_LIST(cid), params],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_LIST(cid), { params }),
    select: (r: any) => ({ data: r.data.data as any[], total: r.data.extra?.pagination?.rows ?? 0 }),
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useJobCreate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_JOBS_CREATE(cid), data),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOBS_LIST(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

export const useJobUpdate = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_JOBS_UPDATE(cid), data),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOBS_LIST(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

export const useJobDelete = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.post(API_ENDPOINTS.CJ_JOBS_DELETE(cid), { id }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOBS_LIST(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

// ── Dropdowns ──────────────────────────────────────────────────────────────

export const useJobClients = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_CLIENTS(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_CLIENTS(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useJobUsers = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_USERS(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_USERS(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

// ── Custom Fields ──────────────────────────────────────────────────────────

export const useJobCFDefs = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_CF_DEFS(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_CF_DEFS(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useJobCFValues = (cid: number, jobId: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_CF_VALUES(cid, jobId)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_CF_VALUES(cid, jobId)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid && !!jobId,
    refetchOnWindowFocus: false,
  });

export const useSaveJobCFValues = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { job_id: number; values: any[] }) =>
      client.post(API_ENDPOINTS.CJ_JOBS_CF_VALUES_SAVE(cid), data),
    onSuccess: (r, vars) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOBS_CF_VALUES(cid, vars.job_id)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

// ── Form Templates ─────────────────────────────────────────────────────────

export const useJobFormTemplates = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_FORMS(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_FORMS(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useJobFormTemplateDetail = (cid: number, id: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_FORM_DETAIL(cid, id)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_FORM_DETAIL(cid, id)),
    select: (r: any) => r.data.data as any,
    enabled: !!cid && !!id,
    refetchOnWindowFocus: false,
  });

// ── Form Responses ─────────────────────────────────────────────────────────

export const useJobFormResponses = (cid: number, jobId: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_JOBS_FORM_RESPONSES(cid, jobId)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_JOBS_FORM_RESPONSES(cid, jobId)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid && !!jobId,
    refetchOnWindowFocus: false,
  });

export const useSaveFormResponses = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_JOBS_FORM_SAVE(cid), data),
    onSuccess: (r, vars) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_JOBS_FORM_RESPONSES(cid, vars.job_id)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

// ── Job Tasks ──────────────────────────────────────────────────────────────

export const useJobTasks = (cid: number, jobId: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_TASKS(cid, jobId)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_TASKS(cid, jobId)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid && !!jobId,
    refetchOnWindowFocus: false,
  });

const useTaskMut = (cid: number, url: (c: number) => string, jobId: number | null, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(url(cid), data),
    onSuccess: (r) => {
      if (jobId) qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_TASKS(cid, jobId)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

export const useTaskCreate = (cid: number, jobId: number | null, cb?: CB) =>
  useTaskMut(cid, API_ENDPOINTS.CJ_TASK_CREATE, jobId, cb);
export const useTaskUpdate = (cid: number, jobId: number | null, cb?: CB) =>
  useTaskMut(cid, API_ENDPOINTS.CJ_TASK_UPDATE, jobId, cb);
export const useTaskDelete = (cid: number, jobId: number | null, cb?: CB) =>
  useTaskMut(cid, API_ENDPOINTS.CJ_TASK_DELETE, jobId, cb);

// ── Checklist Templates + Responses ───────────────────────────────────────

export const useChecklistTemplates = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_CHECKLIST_TEMPLATES(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_CHECKLIST_TEMPLATES(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useChecklistResponses = (cid: number, jobId: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_CHECKLIST_RESPONSES(cid, jobId)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_CHECKLIST_RESPONSES(cid, jobId)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid && !!jobId,
    refetchOnWindowFocus: false,
  });

export const useSaveChecklistResponse = (cid: number, jobId: number | null, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_CHECKLIST_RESPONSE_SAVE(cid), data),
    onSuccess: (r) => {
      if (jobId) qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_CHECKLIST_RESPONSES(cid, jobId)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

// ── Employee Locations ─────────────────────────────────────────────────────

export const useEmployeeLocations = (cid: number) =>
  useQuery({
    queryKey: [API_ENDPOINTS.CJ_EMPLOYEE_LOCATIONS(cid)],
    queryFn: () => client.get(API_ENDPOINTS.CJ_EMPLOYEE_LOCATIONS(cid)),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

export const useUpsertEmployeeLocation = (cid: number, cb: CB = {}) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => client.post(API_ENDPOINTS.CJ_EMPLOYEE_LOCATION_UPSERT(cid), data),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.CJ_EMPLOYEE_LOCATIONS(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};
